import './sentry-config'
import { Auth0Provider } from '@auth0/auth0-react'
import React, { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { PostHogProvider as PostHog } from 'posthog-js/react'
import 'react-toastify/dist/ReactToastify.css'
import './App.css'
import 'components/delete-after-design-package-installation/globals.css'
import * as serviceWorker from './serviceWorker'
import { App } from './App'
import { HubSpotOAuthBoundary } from './pages/oauth/hubspot'
import { parentNodeDebugger } from './utils/parent-node-debugger'
import {
	Auth0Config,
	PostHogConfig,
	FeatureFlagConfig,
	AppConfig
} from 'config'
import { FeatureFlagProviderComponent } from './services/feature-flags'
import {
	appendMarketingParams,
	getMarketingParams,
	setMarketingParams
} from './utils/marketing-params'
import { initTheme } from 'utils/theme'
import { useResolvedTheme } from 'hooks'

// Apply the stored theme preference (cg-theme) before first paint so the app
// boots in the right theme; 'system' keeps tracking the OS while open.
initTheme()

const options = {
	api_host: PostHogConfig.REACT_APP_PUBLIC_POSTHOG_HOST,
	ui_host: PostHogConfig.REACT_APP_PUBLIC_UI_HOST
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false // TODO consider
		}
	}
})

const RootApp = () => {
	const navigate = useNavigate()
	const resolvedTheme = useResolvedTheme()
	parentNodeDebugger()

	useEffect(() => {
		const metaTag = document.querySelector("meta[name='viewport']")
		if (metaTag) {
			if (
				window.innerWidth < 1024 &&
				window.location.pathname.includes('admin')
			) {
				metaTag.setAttribute('content', 'width=device-width, initial-scale=0.1')
			} else {
				metaTag.setAttribute('content', 'width=device-width, initial-scale=1')
			}
		}
	}, [])

	return (
		<Auth0Provider
			domain={Auth0Config.domain}
			clientId={Auth0Config.client_id}
			redirectUri={window.location.origin}
			audience={Auth0Config.audience}
			scope={Auth0Config.scope}
			useRefreshTokens={true}
			cacheLocation={AppConfig.ENV === 'dev' ? 'localstorage' : undefined}
			onRedirectCallback={(state) => {
				// Get existing marketing params from session storage
				const existingParams = getMarketingParams()
				const marketingParams: Record<string, string> = { ...existingParams }

				// Merge with new params from state (if any)
				if (state?.marketingParams) {
					Object.assign(marketingParams, state.marketingParams)
				}

				// Also check URL params (from hosted pages that append to redirect URI)
				const urlParams = new URLSearchParams(window.location.search)
				const auth0InternalParams = [
					'code',
					'state',
					'error',
					'error_description',
					'error_uri'
				]
				urlParams.forEach((value, key) => {
					if (!auth0InternalParams.includes(key) && !marketingParams[key]) {
						marketingParams[key] = value
					}
				})

				// Store marketing params for future use
				setMarketingParams(marketingParams)

				// Get return URL and append marketing params
				let returnTo =
					state?.appState?.returnTo || (state as any)?.returnTo || '/'
				returnTo = appendMarketingParams(returnTo, marketingParams)

				navigate(returnTo)
			}}
		>
			<QueryClientProvider client={queryClient}>
				<PostHog
					options={options}
					apiKey={PostHogConfig.REACT_APP_PUBLIC_POSTHOG_KEY}
				>
					<FeatureFlagProviderComponent config={FeatureFlagConfig}>
						<App />
						<ToastContainer
							position="bottom-center"
							autoClose={5000}
							hideProgressBar={true}
							newestOnTop={false}
							theme={resolvedTheme === 'dark' ? 'dark' : 'colored'}
							className="text-sm"
							closeOnClick
							rtl={false}
							pauseOnFocusLoss
							draggable
							pauseOnHover
							limit={1}
							style={{ maxWidth: '75%', width: 'auto' }}
						/>
						<ReactQueryDevtools initialIsOpen={false} />
					</FeatureFlagProviderComponent>
				</PostHog>
			</QueryClientProvider>
		</Auth0Provider>
	)
}

const container = document.getElementById('root')!
const root = createRoot(container)
root.render(
	<React.StrictMode>
		<BrowserRouter>
			<HubSpotOAuthBoundary>
				<RootApp />
			</HubSpotOAuthBoundary>
		</BrowserRouter>
	</React.StrictMode>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
